import { supabase } from '@/core/database/supabase';
import type { WorkOrderListItem, WorkOrderKpi } from '../types/workOrders.types';

export const workOrdersService = {
  async getWorkOrders(projectId?: string): Promise<WorkOrderListItem[]> {
    let query = supabase
      .from('work_orders')
      .select(`
        *,
        projects(name),
        project_zones(zone_name),
        lead:user_profiles!lead_user_id(full_name, avatar_path)
      `)
      .order('created_at', { ascending: false });

    if (projectId) {
      query = query.eq('project_id', projectId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[workOrdersService] getWorkOrders error:', error);
      return [];
    }

    return (data || []).map(row => ({
      id: row.id,
      code: row.work_order_code || '---',
      title: row.title || 'Không có tiêu đề',
      projectName: row.projects?.name || '---',
      zoneName: row.project_zones?.zone_name || '---',
      status: row.status_code || 'draft',
      priority: row.priority_code || 'medium',
      progress: row.progress_percent !== undefined && row.progress_percent !== null 
        ? row.progress_percent 
        : (Number(row.planned_quantity) > 0 ? (Number(row.actual_quantity) / Number(row.planned_quantity)) * 100 : 0),
      startDate: row.planned_start_at || row.scheduled_date || '',
      endDate: row.planned_end_at || row.due_date || '',
      leadName: row.lead?.full_name || 'Chưa phân công',
      leadAvatar: row.lead?.avatar_path || null,
      type: row.work_type || 'construction',
    }));
  },

  async getKpis(projectId?: string): Promise<WorkOrderKpi> {
    let query = supabase
      .from('work_orders')
      .select('status_code, due_date');

    if (projectId) {
      query = query.eq('project_id', projectId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[workOrdersService] getKpis error:', error);
      return { total: 0, inProgress: 0, completed: 0, delayed: 0 };
    }

    const now = new Date();
    return (data || []).reduce((acc, row) => {
      acc.total++;
      if (row.status_code === 'in_progress') acc.inProgress++;
      if (row.status_code === 'completed' || row.status_code === 'closed') acc.completed++;
      
      const dueDate = row.due_date ? new Date(row.due_date) : null;
      if (dueDate && dueDate < now && row.status_code !== 'completed' && row.status_code !== 'closed') {
        acc.delayed++;
      }
      
      return acc;
    }, { total: 0, inProgress: 0, completed: 0, delayed: 0 });
  },

  async deleteWorkOrder(id: string): Promise<void> {
    // 1. Get data before deleting
    const { data: beforeData } = await supabase
      .from('work_orders')
      .select('*')
      .eq('id', id)
      .single();

    // 2. Perform delete
    const { error } = await supabase
      .from('work_orders')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(error.message);
    }

    // 3. Record Audit Log
    try {
      const { auditService } = await import('@/services/audit.service').then(m => ({ auditService: m.default }));
      await auditService.log({
        project_id: beforeData?.project_id,
        action_code: 'delete',
        module_code: 'work_orders',
        entity_type: 'work_orders',
        entity_id: id,
        summary: `Xóa lệnh thi công: ${beforeData?.work_order_code || id}`,
        before_data: beforeData as any
      });
    } catch (logErr) {
      console.error('[workOrdersService] Failed to record audit log:', logErr);
    }
  }
};
